import { Component, Input, OnChanges, SimpleChanges, ElementRef, AfterViewInit } from '@angular/core';
import * as c3 from 'c3';
import { timeFormat } from 'd3-time-format';

@Component({
    selector: 'app-c3-ci-time-series',
    templateUrl: './c3-ci-time-series.component.html',
    styleUrls: ['./c3-ci-time-series.component.scss'],
    standalone: false
})
export class C3CiTimeSeriesComponent implements OnChanges, AfterViewInit {
  @Input() data: any;
  chart: any;

  constructor(private elRef: ElementRef) { }
  ngAfterViewInit(): void {
    if (this.data) {
      this.generateChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.generateChart();
    }
  }



  generateChart(): void {
    if (!this.data || !this.data.length) {
      this.elRef.nativeElement.querySelector('#simpleTimeChart').innerHTML = 'No Data';
      return;
    }

    const historicalData = this.data.filter(d => d.type === 'Historical' && d.value !== undefined && d.value !== null);

    // const latestData = this.data.filter(d => d.type === 'Latest' && d.value !== undefined && d.value !== null);

    const forecastData = this.data.filter(d => d.type === 'Forecast' && d.value !== undefined && d.value !== null);

    if (!historicalData.length && !forecastData.length) {
      console.warn('No valid Historical Forecast and Latest Data');
      this.elRef.nativeElement.querySelector('#simpleTimeChart').innerHTML = 'No Data';
      return;
    }

    historicalData.forEach(d => d.tsepoch = new Date(d.tsepoch));
    forecastData.forEach(d => d.tsepoch = new Date(d.tsepoch));

    // make duplicate timestamps unique within each series by adding tiny millisecond offsets
    function ensureUniqueTimestamps(arr: any[]) {
      const counts: { [ms: number]: number } = {};
      // sort to keep relative order stable
      arr.sort((a, b) => a.tsepoch.getTime() - b.tsepoch.getTime());
      arr.forEach(item => {
        const base = item.tsepoch.getTime();
        const cnt = counts[base] || 0;
        if (cnt > 0) {
          // add small offset per duplicate (1 ms). Increase if still overlapping visually.
          item.tsepoch = new Date(base + cnt);
        }
        counts[base] = cnt + 1;
      });
    }

    ensureUniqueTimestamps(historicalData);
    ensureUniqueTimestamps(forecastData);


    // const allTimes = [...historicalData.map(d => d.convertedTS),
    // ...forecastData.map(d => d.convertedTS),
    //   // ...latestData.map(d => d.convertedTS)
    // ];
    // 1. Build a sorted, unique list of all timestamps

    const allTimes = Array.from(new Set([
      ...historicalData.map(d => d.tsepoch.getTime()),
      ...forecastData.map(d => d.tsepoch.getTime())
    ])).sort((a, b) => a - b).map(ts => new Date(ts));

    // 2. Map values to the unified time axis
    function alignValues(dataArr, allTimes) {
      const valueMap = new Map(dataArr.map(d => [d.tsepoch.getTime(), d.value]));
      return allTimes.map(ts => valueMap.get(ts.getTime()) ?? null);
    }

    const historicalValues = alignValues(historicalData, allTimes);
    const forecastValues = alignValues(forecastData, allTimes);



    // ensure historicalData and forecastData already have Date objects (you do this above)
    // create per-series time and value arrays so each series uses its own x-axis
    const histTimes = historicalData.map(d => d.tsepoch);
    const histVals = historicalData.map(d => d.value);
    const fcTimes = forecastData.map(d => d.tsepoch);
    const solidLineData = forecastData.filter(d => d.isConfirmed);
    const fcVals = forecastData.map(d => d.value);
    // const fcDash = fcTimes && fcTimes.length > 3 ? fcTimes[3] : null;
    const fcDash = solidLineData[solidLineData.length - 1].tsepoch
    this.chart = c3.generate({
      bindto: this.elRef.nativeElement.querySelector('#simpleTimeChart'),
      data: {
        // map series to their own x columns
        xs: {
          "Actual value": 'x_historical',
          Forecast: 'x_forecast'
        },
        columns: [
          ['x_historical', ...histTimes],
          ['Actual value', ...histVals],
          ['x_forecast', ...fcTimes],
          ['Forecast', ...fcVals]
        ],
        types: {
          "Actual value": 'line',
          Forecast: 'line'
        },
        colors: {
          "Actual value": '#23adcfff',
          Forecast: '#03346E'
        },
        classes: {
          Forecast: 'forecast-line'
        },
        regions: fcDash ? {
          Forecast: [
            {
              start: fcDash,
              style: 'dashed'
            }
          ]
        } : {}
      },
      axis: {
        x: {
          type: 'timeseries',
          tick: {
            format: '%d %b %y %H:%M',
            count: 10
          },
          label: {
            text: 'Date',
            position: 'outer-center'

          }
        },
        y: {
          label: {
            text: 'Average',
            position: 'outer-middle'
          }
        }
      },
      point: {
        show: true,
        r: 3
      },
      tooltip: {
        format: {
          title: (x) =>
            timeFormat('%d %b %Y, %H:%M')(new Date(x as string)),
          value: (v) => v != null ? Number(v).toFixed(2) : '-'
        }
      },
      grid: {
        x: { show: true },
        y: { show: true }
      },
      zoom: {
        enabled: true
      },
      onrendered: () => {
        const dashedPaths = this.elRef.nativeElement.querySelectorAll('.c3-line-Forecast.c3-region-dashed, .c3-region-dashed');

        dashedPaths.forEach((el: any) => {
          el.style.strokeDasharray = '5,5';
          el.style.strokeWidth = '3px'
        });
      },
    });

    setTimeout(() => {
      const forecastLine = this.elRef.nativeElement.querySelector('.c3-line-Forecast');
      if (forecastLine) {
        // forecastLine.style.strokeDasharray = '5,5';
        forecastLine.style.strokeWidth = '2px';
      }
    }, 300);
  }
}
